import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../foundations/Icon';
import Tooltip from './Tooltip';
import css from './CopyToClipboardButton.css';

const COPY_TIMEOUT = 1500;
const COPIED_TOOLTIP_TEXT = 'STATIC.TOOLTIPS.COPIED';

const CopyToClipboardButton = ({
    textToCopy,
    preCoyIconName = 'copy',
    tooltip = null,
    onClick: onClickProp,
    buttonClassName = null,
    copiedButtonClassName = '',
    ariaLabel = 'copy to clipboard',
    labelTranslationKey = null,
    iconSize = IconSizes.MEDIUM,
    copiedIconSize = iconSize,
    iconVariant = IconVariants.LIGHT,
    copiedIconVariant = IconVariants.SUCCESS,
}) => {
    const [copied, setCopied] = useState(false);
    const resetTimerRef = useRef(null);

    useEffect(
        () => () => {
            window.clearTimeout(resetTimerRef.current);
        },
        []
    );

    const onClick = e => {
        e.stopPropagation();

        onClickProp && onClickProp();

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                window.clearTimeout(resetTimerRef.current);
                resetTimerRef.current = window.setTimeout(() => setCopied(false), COPY_TIMEOUT);
            })
            .catch(() => {});
    };

    const iconElement = (
        <Icon
            name={copied ? 'check' : preCoyIconName}
            size={copied ? copiedIconSize : iconSize}
            variant={copied ? copiedIconVariant : iconVariant}
            onClick={buttonClassName ? undefined : onClick}
            interactive={!buttonClassName}
        />
    );

    const tooltipTitle = copied ? COPIED_TOOLTIP_TEXT : tooltip;

    if (buttonClassName) {
        const button = (
            <button
                type="button"
                aria-label={ariaLabel}
                className={classNames(buttonClassName, copied && copiedButtonClassName)}
                onClick={onClick}
            >
                {iconElement}
                {labelTranslationKey && <Translate id={labelTranslationKey} />}
            </button>
        );

        return tooltipTitle ? (
            <Tooltip titleTranslationKey={tooltipTitle} wrapperClass={css.tooltipWrapper} style={{ height: '100%' }}>
                {button}
            </Tooltip>
        ) : (
            button
        );
    }

    return tooltipTitle ? (
        <Tooltip titleTranslationKey={tooltipTitle} wrapperClass={css.tooltipWrapper}>
            {iconElement}
        </Tooltip>
    ) : (
        iconElement
    );
};

CopyToClipboardButton.propTypes = {
    preCoyIconName: PropTypes.string,
    textToCopy: PropTypes.string.isRequired,
    tooltip: PropTypes.string,
    onClick: PropTypes.func,
    buttonClassName: PropTypes.string,
    copiedButtonClassName: PropTypes.string,
    ariaLabel: PropTypes.string,
    labelTranslationKey: PropTypes.string,
    iconSize: PropTypes.oneOf(Object.values(IconSizes)),
    copiedIconSize: PropTypes.oneOf(Object.values(IconSizes)),
    iconVariant: PropTypes.oneOf(Object.values(IconVariants)),
    copiedIconVariant: PropTypes.oneOf(Object.values(IconVariants)),
};

export default CopyToClipboardButton;
